import { getFeedbackQuestion, orUndefined } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@lms/ui/card';
import { LoaderCircle, TrashIcon } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import UpdateQuestionForm from '@/features/survey/components/update-question';
import { NotFound } from '@/routes/not-found';

// Ports app/[brand]/admin/surveys/question/[id]/page.tsx. The Next server
// component parsed the id, rejected ids <= 0 with `notFound()`, and awaited the
// feedback question; here the id comes from `useParams`, the question loads via
// SWR, an invalid/missing question renders <NotFound />, and the Next redirect
// on save becomes a `useNavigate` back to the surveys list. Super-admin only.
// Renders CONTENT only; the admin `<Shell>` wraps this route.
export function QuestionDetail() {
  const t = useTranslations('admin');
  const { slug } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);
  const isSuperAdmin = access?.superAdmin ?? false;
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const id = Number.parseInt(idParam ?? '', 10);
  const validId = !Number.isNaN(id) && id > 0;

  const { data: question, isLoading: questionLoading } = useSWR(
    validId && isSuperAdmin ? ['admin/feedback-question', id] : null,
    () => orUndefined(getFeedbackQuestion(id)),
  );

  if (roleLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <PageBox>
        <PageHeader backHref={`/${slug}/admin/surveys`}>
          <PageTitle>{t('surveys')}</PageTitle>
        </PageHeader>
        <p className="text-muted-foreground">{t('unauthorized')}</p>
      </PageBox>
    );
  }

  if (!validId) {
    return <NotFound />;
  }

  if (questionLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!question) {
    return <NotFound />;
  }

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('surveys')}</PageTitle>
        <PageButtons>
          <Button variant="destructive" asChild>
            <Link to={`/${slug}/admin/surveys`}>
              <TrashIcon className="size-4" />
              {t('discard')}
            </Link>
          </Button>
        </PageButtons>
      </PageHeader>
      <Card>
        <CardHeader>
          <CardTitle>
            {t('add', { what: t('question').toLowerCase() })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <UpdateQuestionForm
            question={question}
            onSuccess={() => navigate(`/${slug}/admin/surveys`)}
          />
        </CardContent>
      </Card>
    </PageBox>
  );
}
